import { formatOrderItemVariantMeta } from "./variantDisplay";

/** Separator for size|color composite keys (unlikely in product labels). */
export const SIZE_COLOR_STOCK_SEP = "\x1e";

/** @param {string} size @param {string} color */
export function comboStockKey(size, color) {
  return `${String(size ?? "").trim()}${SIZE_COLOR_STOCK_SEP}${String(color ?? "").trim()}`;
}

/** @param {string} key */
export function parseComboStockKey(key) {
  const raw = String(key ?? "");
  const i = raw.indexOf(SIZE_COLOR_STOCK_SEP);
  if (i < 0) return { size: raw.trim(), color: "" };
  return { size: raw.slice(0, i).trim(), color: raw.slice(i + 1).trim() };
}

/** @param {unknown} value */
export function normalizeOptionList(value) {
  if (value == null) return [];
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        return parsed.map((x) => String(x ?? "").trim()).filter(Boolean);
      }
    } catch {
      return [s];
    }
    return [];
  }
  if (!Array.isArray(value)) return [];
  return value.map((x) => String(x ?? "").trim()).filter(Boolean);
}

/** Resolve storefront price for a size/option label from extra_prices rows. */
export function resolveOptionPriceFromExtras(optionLabel, extraPrices, basePrice = 0) {
  const sz = String(optionLabel ?? "").trim();
  const fallback = Number(basePrice);
  const base = Number.isFinite(fallback) ? fallback : 0;
  if (!sz) return base;
  const extras = Array.isArray(extraPrices) ? extraPrices : [];
  for (const p of extras) {
    const label = String(p?.description_ar || p?.description_en || "").trim();
    if (label === sz || label.startsWith(`${sz} - `)) {
      const n = Number(p?.price);
      return Number.isFinite(n) ? n : base;
    }
  }
  const allSizesRow = extras.find((p) => {
    const label = String(p?.description_ar || p?.description_en || "").trim();
    return label === "جميع الأحجام" || label.startsWith("جميع الأحجام - ");
  });
  if (allSizesRow) {
    const n = Number(allSizesRow?.price);
    return Number.isFinite(n) ? n : base;
  }
  return base;
}

/** Sort size/color labels by matching extra_prices (lowest price first). */
export function sortOptionListByPrice(options, extraPrices, basePrice = 0) {
  const list = normalizeOptionList(options);
  if (list.length <= 1) return list;
  const indexed = list.map((label, index) => ({ label, index }));
  indexed.sort((a, b) => {
    const pa = resolveOptionPriceFromExtras(a.label, extraPrices, basePrice);
    const pb = resolveOptionPriceFromExtras(b.label, extraPrices, basePrice);
    if (pa !== pb) return pa - pb;
    return a.index - b.index;
  });
  return indexed.map((x) => x.label);
}

/** Sort variant price rows for display (lowest price first). */
export function sortExtraPricesList(extraPrices) {
  if (!Array.isArray(extraPrices) || extraPrices.length <= 1) {
    return Array.isArray(extraPrices) ? extraPrices : [];
  }
  return [...extraPrices].sort((a, b) => {
    const pa = Number(a?.price);
    const pb = Number(b?.price);
    const na = Number.isFinite(pa) ? pa : 0;
    const nb = Number.isFinite(pb) ? pb : 0;
    return na - nb;
  });
}

/**
 * @param {Record<string, unknown>} product
 * @param {string|null|undefined} selectedSize
 * @param {string|null|undefined} selectedColor
 * @param {'ar'|'en'} [lang]
 * @returns {{ message: string, focus: 'size'|'color' }|null}
 */
export function productVariantSelectionError(
  product,
  selectedSize,
  selectedColor,
  lang = "ar"
) {
  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);
  const needsSize = sizes.length > 0;
  const needsColor = colors.length > 0;
  if (!needsSize && !needsColor) return null;

  const hasSize = Boolean(String(selectedSize ?? "").trim());
  const hasColor = Boolean(String(selectedColor ?? "").trim());
  const ar = lang !== "en";

  if (needsSize && needsColor) {
    if (!hasSize && !hasColor) {
      return {
        message: ar
          ? "يرجى اختيار الحجم واللون قبل الإضافة إلى السلة"
          : "Please select size and color before adding to cart",
        focus: "size",
      };
    }
    if (!hasSize) {
      return {
        message: ar
          ? "يرجى اختيار الحجم أيضاً قبل الإضافة إلى السلة"
          : "Please also select a size before adding to cart",
        focus: "size",
      };
    }
    if (!hasColor) {
      return {
        message: ar
          ? "يرجى اختيار اللون أيضاً قبل الإضافة إلى السلة"
          : "Please also select a color before adding to cart",
        focus: "color",
      };
    }
    return null;
  }

  if (needsSize && !hasSize) {
    return {
      message: ar
        ? "يرجى اختيار الحجم قبل الإضافة إلى السلة"
        : "Please select a size before adding to cart",
      focus: "size",
    };
  }
  if (needsColor && !hasColor) {
    return {
      message: ar
        ? "يرجى اختيار اللون قبل الإضافة إلى السلة"
        : "Please select a color before adding to cart",
      focus: "color",
    };
  }
  return null;
}

/** Loose match for Arabic variant labels (أزرق vs ازرق, etc.). */
export function normalizeLabelCompare(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\u0623\u0625\u0622\u0671]/g, "\u0627")
    .replace(/\u0649/g, "\u064A");
}

/** Map a stored label to the canonical option from the product list. */
export function matchOptionLabel(raw, options) {
  const needle = String(raw ?? "").trim();
  if (!needle) return "";
  const list = normalizeOptionList(options);
  if (list.includes(needle)) return needle;
  const norm = normalizeLabelCompare(needle);
  for (const opt of list) {
    if (normalizeLabelCompare(opt) === norm) return opt;
  }
  return needle;
}

/**
 * Find variant_stock map key for size+color (exact or normalized label match).
 * @param {Record<string, number>|null} vmap
 */
export function resolveComboStockKey(vmap, size, color) {
  if (!vmap) return null;
  const sz = String(size ?? "").trim();
  const cz = String(color ?? "").trim();
  if (!sz || !cz) return null;
  const exact = comboStockKey(sz, cz);
  if (Object.prototype.hasOwnProperty.call(vmap, exact)) return exact;
  const nsz = normalizeLabelCompare(sz);
  const ncz = normalizeLabelCompare(cz);
  for (const k of Object.keys(vmap)) {
    const { size: ks, color: kc } = parseComboStockKey(k);
    if (normalizeLabelCompare(ks) === nsz && normalizeLabelCompare(kc) === ncz) {
      return k;
    }
  }
  return null;
}

/**
 * Align order-edit line size/color labels to the product's canonical option list.
 * @param {Record<string, unknown>} line
 * @param {Record<string, unknown>} product
 */
export function alignLineVariantsToProduct(line, product) {
  if (!line || !product) return line;
  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);
  const selected_size = matchOptionLabel(line.selected_size, sizes);
  const selected_color = matchOptionLabel(line.selected_color, colors);
  const initial_size = matchOptionLabel(line.initial_size, sizes);
  const initial_color = matchOptionLabel(line.initial_color, colors);
  if (
    selected_size === (line.selected_size || "") &&
    selected_color === (line.selected_color || "") &&
    initial_size === (line.initial_size || "") &&
    initial_color === (line.initial_color || "")
  ) {
    return line;
  }
  return {
    ...line,
    selected_size,
    selected_color,
    initial_size,
    initial_color,
  };
}

/** @param {unknown} raw */
export function coerceVariantStockMap(raw) {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const sk = String(k ?? "").trim();
    if (!sk) continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    out[sk] = Math.max(0, Math.floor(n));
  }
  return Object.keys(out).length ? out : null;
}

/** @param {Record<string, unknown>} product */
export function usesComboVariantStock(product) {
  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);
  const vmap = coerceVariantStockMap(product?.variant_stock);
  return sizes.length > 0 && colors.length > 0 && vmap != null;
}

/** True when any stock field is set (simple total or per size/color/combo maps). */
export function productHasAnyStockTracked(product) {
  if (!product || typeof product !== "object") return false;

  const stock = product.stock;
  if (stock !== null && stock !== undefined && String(stock).trim() !== "") {
    const n = Number(stock);
    if (Number.isFinite(n)) return true;
  }

  for (const raw of [product.variant_stock, product.size_stock, product.color_stock]) {
    const map = coerceVariantStockMap(raw);
    if (map && Object.keys(map).length > 0) return true;
  }

  return false;
}

/**
 * @param {Record<string, unknown>} product
 * @returns {{ tracked: boolean, totalQty: number | null, optionCount: number }}
 */
export function getProductStockOverview(product) {
  if (!product || typeof product !== "object") {
    return { tracked: false, totalQty: null, optionCount: 0 };
  }

  const stock = product.stock;
  if (stock !== null && stock !== undefined && String(stock).trim() !== "") {
    const n = Number(stock);
    if (Number.isFinite(n)) {
      return {
        tracked: true,
        totalQty: Math.max(0, Math.floor(n)),
        optionCount: 1,
      };
    }
  }

  const qtys = [];
  for (const raw of [product.variant_stock, product.size_stock, product.color_stock]) {
    const map = coerceVariantStockMap(raw);
    if (!map) continue;
    for (const v of Object.values(map)) {
      qtys.push(Math.max(0, Math.floor(Number(v) || 0)));
    }
  }

  if (qtys.length === 0) {
    return { tracked: false, totalQty: null, optionCount: 0 };
  }

  return {
    tracked: true,
    totalQty: qtys.reduce((sum, q) => sum + q, 0),
    optionCount: qtys.length,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} map
 * @param {string | null | undefined} key
 * @returns {null|number} null = not tracked / unlimited; number = remaining
 */
export function variantTrackedQty(map, key) {
  const k = String(key ?? "").trim();
  if (!k || !map || typeof map !== "object") return null;
  if (!Object.prototype.hasOwnProperty.call(map, k)) return null;
  return Math.max(0, Math.floor(Number(map[k]) || 0));
}

/**
 * @param {Record<string, unknown>} product
 * @param {'size'|'color'} kind
 * @param {string} label
 * @param {string | null | undefined} otherSelected
 */
export function optionDepleted(product, kind, label, otherSelected = null) {
  if (usesComboVariantStock(product)) {
    const vmap = coerceVariantStockMap(product?.variant_stock);
    const lab = String(label ?? "").trim();
    const other = String(otherSelected ?? "").trim();
    if (!vmap || !lab) return false;

    if (other) {
      const sz = kind === "size" ? lab : other;
      const cz = kind === "color" ? lab : other;
      const mapKey = resolveComboStockKey(vmap, sz, cz);
      const q =
        mapKey != null ? Math.max(0, Math.floor(Number(vmap[mapKey]) || 0)) : null;
      return q !== null && q <= 0;
    }

    let hasTracked = false;
    const labNorm = normalizeLabelCompare(lab);
    for (const [k, v] of Object.entries(vmap)) {
      const { size, color } = parseComboStockKey(k);
      const matches =
        kind === "size"
          ? normalizeLabelCompare(size) === labNorm
          : normalizeLabelCompare(color) === labNorm;
      if (!matches) continue;
      hasTracked = true;
      if (Number(v) > 0) return false;
    }
    return hasTracked;
  }

  const smap = coerceVariantStockMap(product?.size_stock);
  const cmap = coerceVariantStockMap(product?.color_stock);
  const q =
    kind === "size" ? variantTrackedQty(smap, label) : variantTrackedQty(cmap, label);
  if (q === null) return false;
  return q <= 0;
}

/** True when the product cannot be purchased in any configuration. */
export function productGloballyOutOfStock(product) {
  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);
  const vmap = coerceVariantStockMap(product?.variant_stock);
  const smap = coerceVariantStockMap(product?.size_stock);
  const cmap = coerceVariantStockMap(product?.color_stock);

  if (usesComboVariantStock(product)) {
    let hasTracked = false;
    for (const v of Object.values(vmap)) {
      hasTracked = true;
      if (Number(v) > 0) return false;
    }
    return hasTracked;
  }

  if (sizes.length === 0 && colors.length === 0) {
    const st = product?.stock;
    if (st == null || st === "") return false;
    return Number(st) === 0;
  }

  const dimExhausted = (labels, mmap) => {
    if (!labels.length) return false;
    for (const lab of labels) {
      const q = variantTrackedQty(mmap, lab);
      if (q === null || q > 0) return false;
    }
    return true;
  };

  return dimExhausted(sizes, smap) || dimExhausted(colors, cmap);
}

/**
 * @param {Record<string, unknown>} product
 * @param {string | null | undefined} selectedSize
 * @param {string | null | undefined} selectedColor
 */
export function maxQtyForSelection(product, selectedSize, selectedColor) {
  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);
  const vmap = coerceVariantStockMap(product?.variant_stock);
  const smap = coerceVariantStockMap(product?.size_stock);
  const cmap = coerceVariantStockMap(product?.color_stock);

  if (usesComboVariantStock(product)) {
    const sz = String(selectedSize ?? "").trim();
    const cz = String(selectedColor ?? "").trim();
    if (sz && cz) {
      const mapKey = resolveComboStockKey(vmap, sz, cz);
      if (mapKey != null) {
        return Math.max(0, Math.floor(Number(vmap[mapKey]) || 0));
      }
    }
    return 9999;
  }

  if (sizes.length === 0 && colors.length === 0) {
    const st = product?.stock;
    if (st == null || st === "") return 9999;
    return Math.max(0, Math.floor(Number(st)));
  }

  let cap = 9999;
  if (sizes.length > 0 && selectedSize) {
    const q = variantTrackedQty(smap, selectedSize);
    if (q != null) cap = Math.min(cap, q);
  }
  if (colors.length > 0 && selectedColor) {
    const q = variantTrackedQty(cmap, selectedColor);
    if (q != null) cap = Math.min(cap, q);
  }
  return cap;
}

/**
 * Quantity available for the current size/color selection (wholesale catalog display).
 * Returns null until required options are chosen or stock is not tracked.
 * @param {Record<string, unknown>} product
 * @param {string | null | undefined} selectedSize
 * @param {string | null | undefined} selectedColor
 * @returns {number | null}
 */
/** Cart/order line key for aggregating stock (product + size + color). */
export function cartLineStockKey(item) {
  const pid = String(item?.id ?? item?.product_id ?? "").trim();
  const sz = String(item?.selectedSize ?? item?.selected_size ?? "").trim();
  const cl = String(item?.selectedColor ?? item?.selected_color ?? "").trim();
  return `${pid}\x1f${sz}\x1f${cl}`;
}

/** Normalized addon identity for exact cart-line matching. */
function cartLineAddonKey(addons) {
  const list = Array.isArray(addons) ? addons : [];
  if (!list.length) return "";
  return list
    .map((a) => {
      const id = String(a?.id ?? "").trim();
      const name = String(a?.name ?? "").trim();
      const price = Number(a?.price || 0);
      return `${id}|${name}|${price}`;
    })
    .sort()
    .join(",");
}

/**
 * Exact-match key for merging duplicate cart lines (product + options + note + price).
 */
export function cartLineMergeKey(item) {
  if (item?.isBundleOffer) {
    const mk = String(item.bundleOfferMergeKey || "").trim();
    return `bundle\x1f${mk}`;
  }
  if (item?.isCustomOrder) {
    const sid = String(item.customOrderSectionId || item.id || "").trim();
    const mk = String(item.customOrderMergeKey || sid).trim();
    return `custom\x1f${mk}`;
  }
  const pid = String(item?.id ?? item?.product_id ?? "").trim();
  const sz = String(item?.selectedSize ?? item?.selected_size ?? "").trim();
  const cl = String(item?.selectedColor ?? item?.selected_color ?? "").trim();
  const variant = String(item?.selectedVariant ?? item?.selected_variant ?? "").trim();
  const note = String(item?.note ?? "").trim();
  const addons = cartLineAddonKey(item?.selectedAddons);
  const baseRaw = item?.baseUnitPrice;
  const base =
    baseRaw != null && baseRaw !== "" && Number.isFinite(Number(baseRaw))
      ? Number(baseRaw)
      : Number(item?.price || 0);
  const wholesale =
    item?.wholesale_price != null && item?.wholesale_price !== ""
      ? Number(item.wholesale_price)
      : "none";
  return [
    pid,
    sz,
    cl,
    variant,
    note,
    addons,
    base,
    wholesale,
  ].join("\x1e");
}

/** Add or merge a line into the cart when all options match exactly. */
export function mergeCartLine(cart, nextItem) {
  const key = cartLineMergeKey(nextItem);
  const addQty = Math.max(1, Number(nextItem?.quantity) || 1);
  let merged = false;
  const out = (cart || []).map((item) => {
    if (!merged && cartLineMergeKey(item) === key) {
      merged = true;
      return {
        ...item,
        quantity: Math.max(1, Number(item.quantity) || 1) + addQty,
      };
    }
    return item;
  });
  if (!merged) {
    out.push({ ...nextItem, quantity: addQty });
  }
  return out;
}

/** Collapse duplicate cart lines (e.g. after loading persisted cart). */
export function consolidateCartLines(cart) {
  const order = [];
  const buckets = new Map();
  for (const item of cart || []) {
    const key = cartLineMergeKey(item);
    const qty = Math.max(1, Number(item?.quantity) || 1);
    if (buckets.has(key)) {
      const row = buckets.get(key);
      row.quantity = Math.max(1, Number(row.quantity) || 1) + qty;
    } else {
      buckets.set(key, { ...item, quantity: qty });
      order.push(key);
    }
  }
  return order.map((k) => buckets.get(k));
}

/** Sum quantities in cart for the same product + size + color. */
export function sumCartQtyForStockKey(cart, key, excludeIdx = -1) {
  let sum = 0;
  (cart || []).forEach((item, idx) => {
    if (idx === excludeIdx) return;
    if (cartLineStockKey(item) === key) {
      sum += Math.max(1, Number(item.quantity) || 1);
    }
  });
  return sum;
}

/**
 * Validate aggregated cart demand against variant stock caps.
 * @returns {string|null} Arabic error message or null if OK
 */
export function validateCartStockLimits(cart) {
  const checked = new Set();
  for (let idx = 0; idx < (cart || []).length; idx += 1) {
    const item = cart[idx];
    if (item?.isCustomOrder || item?.isBundleOffer) continue;
    const key = cartLineStockKey(item);
    if (checked.has(key)) continue;
    checked.add(key);
    const total = sumCartQtyForStockKey(cart, key);
    const maxQ = maxQtyForSelection(item, item?.selectedSize, item?.selectedColor);
    if (!Number.isFinite(maxQ) || maxQ >= 9999) continue;
    if (total > maxQ) {
      const extra = formatOrderItemVariantMeta(
        { selected_size: item?.selectedSize, selected_color: item?.selectedColor },
        "ar"
      );
      const suffix = extra ? ` (${extra})` : "";
      return `الكمية المطلوبة (${total}) تتجاوز المتوفر (${maxQ})${suffix}. يرجى تقليل الكمية أو التواصل مع المتجر.`;
    }
  }
  return null;
}

/**
 * Warehouse qty for a size+color combo (no order-line reservation).
 * @returns {number|null} null = not tracked / unlimited
 */
export function warehouseQtyForSelection(product, selectedSize, selectedColor) {
  const cap = maxQtyForSelection(product, selectedSize, selectedColor);
  if (!Number.isFinite(cap) || cap >= 9999) return null;
  return cap;
}

/**
 * Order-edit pill strikethrough: based on warehouse stock only (what the green box shows).
 * An option is available if ANY combo with that size/color has warehouse qty > 0.
 * Reserved qty in a confirmed order affects max quantity, not whether pills look depleted.
 */
export function isOrderEditOptionDepleted(product, kind, label) {
  if (!product) return false;
  const lab = String(label ?? "").trim();
  if (!lab) return false;

  if (!usesComboVariantStock(product)) {
    const sizes = normalizeOptionList(product?.sizes);
    const colors = normalizeOptionList(product?.colors);
    if (kind === "size") {
      const wh = warehouseQtyForSelection(product, matchOptionLabel(lab, sizes), "");
      if (wh === null) return optionDepleted(product, kind, label, null);
      return wh <= 0;
    }
    if (kind === "color") {
      const wh = warehouseQtyForSelection(product, "", matchOptionLabel(lab, colors));
      if (wh === null) return optionDepleted(product, kind, label, null);
      return wh <= 0;
    }
    return optionDepleted(product, kind, label, null);
  }

  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);
  const sizeCandidates =
    kind === "size" ? [matchOptionLabel(lab, sizes)] : sizes.map((s) => matchOptionLabel(s, sizes));
  const colorCandidates =
    kind === "color"
      ? [matchOptionLabel(lab, colors)]
      : colors.map((c) => matchOptionLabel(c, colors));

  if (!sizeCandidates.length || !colorCandidates.length) {
    return optionDepleted(product, kind, label, null);
  }

  for (const sz of sizeCandidates) {
    for (const cz of colorCandidates) {
      const wh = warehouseQtyForSelection(product, sz, cz);
      if (wh === null || wh > 0) return false;
    }
  }
  return true;
}

export function availableQtyForSelection(product, selectedSize, selectedColor) {
  if (!productHasAnyStockTracked(product)) return null;

  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);

  if (sizes.length > 0 && colors.length > 0) {
    if (!String(selectedSize ?? "").trim() || !String(selectedColor ?? "").trim()) {
      return null;
    }
  } else if (sizes.length > 0) {
    if (!String(selectedSize ?? "").trim()) return null;
  } else if (colors.length > 0) {
    if (!String(selectedColor ?? "").trim()) return null;
  }

  const cap = maxQtyForSelection(product, selectedSize, selectedColor);
  if (!Number.isFinite(cap) || cap >= 9999) return null;
  return cap;
}

/**
 * كمية محجوزة مسبقاً في سطر طلب مؤكّد (نفس المنتج والخيارات الأصلية).
 * @param {{ product_id?: string, selected_size?: string, selected_color?: string, initial_product_id?: string, initial_size?: string, initial_color?: string, initial_quantity?: number }} line
 */
export function orderEditLineReservedQty(line) {
  if (String(line?.product_id || "") !== String(line?.initial_product_id || "")) return 0;
  if (normalizeLabelCompare(line?.selected_size) !== normalizeLabelCompare(line?.initial_size)) {
    return 0;
  }
  if (normalizeLabelCompare(line?.selected_color) !== normalizeLabelCompare(line?.initial_color)) {
    return 0;
  }
  return Math.max(0, Math.floor(Number(line?.initial_quantity) || 0));
}

/**
 * أقصى كمية لسطر في طلب مؤكّد:
 * مخزون المستودع (الرقم في إدارة المخزون) + كمية هذا السطر في الطلب.
 * مثال: مخزون 10 وطلبية قديمة 5 → حتى 15 (عند الحفظ يُسترجع 5 ثم يُخصم الجديد).
 */
export function maxQtyForOrderEditLine(product, selectedSize, selectedColor, reservedQty = 0) {
  const warehouse = maxQtyForSelection(product, selectedSize, selectedColor);
  const reserved = Math.max(0, Math.floor(Number(reservedQty) || 0));
  if (warehouse >= 9999) return 9999;
  if (reserved <= 0) return warehouse;
  return warehouse + reserved;
}

/** @param {boolean} wantsSizes @param {boolean} wantsColors */
export function getVariantMode(wantsSizes, wantsColors) {
  if (!wantsSizes && !wantsColors) return "none";
  if (wantsSizes && wantsColors) return "both";
  if (wantsSizes) return "sizes";
  return "colors";
}

/**
 * Validates sizes/colors answers and lists (quantities are never required).
 * @returns {{ ok: boolean, code: null | "questions_unanswered" | "need_size" | "need_color" | "need_size_and_color" }}
 */
export function validateVariantOptions({ wantsSizes, wantsColors, sizes = [], colors = [] }) {
  if (wantsSizes === null || wantsColors === null) {
    return { ok: false, code: "questions_unanswered" };
  }
  const sizeList = normalizeOptionList(sizes);
  const colorList = normalizeOptionList(colors);
  const needsSizes = wantsSizes === true;
  const needsColors = wantsColors === true;

  if (needsSizes && needsColors) {
    if (sizeList.length === 0 && colorList.length === 0) {
      return { ok: false, code: "need_size_and_color" };
    }
    if (sizeList.length === 0) return { ok: false, code: "need_size" };
    if (colorList.length === 0) return { ok: false, code: "need_color" };
    return { ok: true, code: null };
  }
  if (needsSizes && sizeList.length === 0) return { ok: false, code: "need_size" };
  if (needsColors && colorList.length === 0) return { ok: false, code: "need_color" };
  return { ok: true, code: null };
}

/** @param {string[]} sizes @param {string[]} colors @param {Record<string, string>} [prev] */
export function syncComboQtyMap(sizes, colors, prev = {}) {
  const next = {};
  for (const s of sizes) {
    for (const c of colors) {
      const k = comboStockKey(s, c);
      next[k] = prev[k] ?? "";
    }
  }
  return next;
}

/** @param {unknown} raw */
export function comboQtyMapFromVariantStock(raw) {
  const vmap = coerceVariantStockMap(raw);
  if (!vmap) return {};
  const out = {};
  for (const [k, v] of Object.entries(vmap)) {
    out[k] = String(v);
  }
  return out;
}

/** @param {string[]} sizes @param {string[]} colors @param {Record<string, string>} comboQtyByKey */
export function buildComboStockMapFromMatrix(sizes, colors, comboQtyByKey) {
  const entries = [];
  for (const s of sizes) {
    for (const c of colors) {
      entries.push({
        size: s,
        color: c,
        qty: comboQtyByKey[comboStockKey(s, c)] ?? "",
      });
    }
  }
  return buildComboStockMapFromEntries(entries);
}

/** @param {Array<{id?: string, color: string, size: string, qty: string}>} entries */
export function buildComboStockMapFromEntries(entries) {
  const m = {};
  for (const e of entries || []) {
    const color = String(e?.color ?? "").trim();
    const size = String(e?.size ?? "").trim();
    const qRaw = String(e?.qty ?? "").trim();
    if (!color || !size || qRaw === "" || !Number.isFinite(Number(qRaw))) continue;
    m[comboStockKey(size, color)] = Math.max(0, Math.floor(Number(qRaw)));
  }
  return Object.keys(m).length ? m : null;
}

/** @param {unknown} raw @param {string[]} sizes @param {string[]} colors */
/** Build FormData stock fields for product create/update. */
export function appendVariantStockToFormData(formData, opts) {
  const {
    canUseVariantStock,
    enableSizes,
    enableColors,
    sizes = [],
    colors = [],
    sizeStockByKey = {},
    colorStockByKey = {},
    comboQtyByKey = {},
    stock = "",
    isUpdate = false,
  } = opts;
  if (!canUseVariantStock) return;
  const mode = getVariantMode(enableSizes, enableColors);
  if (mode === "none") {
    formData.append("variant_stock", "");
    formData.append("size_stock", "");
    formData.append("color_stock", "");
    const q = String(stock ?? "").trim();
    if (q !== "" && Number.isFinite(Number(q))) {
      formData.append("stock", String(Math.max(0, Math.floor(Number(q)))));
    } else if (isUpdate) {
      formData.append("stock", "");
    }
    return;
  }
  if (mode === "both") {
    const comboMap = buildComboStockMapFromMatrix(sizes, colors, comboQtyByKey);
    formData.append("variant_stock", comboMap ? JSON.stringify(comboMap) : "");
    formData.append("size_stock", "");
    formData.append("color_stock", "");
    return;
  }
  formData.append("variant_stock", "");
  if (enableSizes) {
    const m = {};
    for (const s of sizes) {
      const q = String(sizeStockByKey[s] ?? "").trim();
      if (q !== "" && Number.isFinite(Number(q))) m[s] = Math.max(0, Math.floor(Number(q)));
    }
    formData.append("size_stock", Object.keys(m).length ? JSON.stringify(m) : "");
  }
  if (enableColors) {
    const m = {};
    for (const c of colors) {
      const q = String(colorStockByKey[c] ?? "").trim();
      if (q !== "" && Number.isFinite(Number(q))) m[c] = Math.max(0, Math.floor(Number(q)));
    }
    formData.append("color_stock", Object.keys(m).length ? JSON.stringify(m) : "");
  }
}

export function comboEntriesFromVariantStock(raw, sizes, colors) {
  const vmap = coerceVariantStockMap(raw);
  if (!vmap) return [];
  const mkId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `combo_${Date.now()}_${Math.random()}`;
  return Object.entries(vmap).map(([key, val]) => {
    const { size, color } = parseComboStockKey(key);
    return {
      id: mkId(),
      size: size || sizes[0] || "",
      color: color || colors[0] || "",
      qty: String(val),
    };
  });
}
